<script lang="ts">
  import Card from '../../components/ui/Card.svelte';
  import Button from '../../components/ui/Button.svelte';
  import RadioGroup from '../../components/ui/RadioGroup.svelte';
  import { withPageTitle } from '../../lib/page-title';
  import { startCheckout, ApiError, type DonateSku } from '../../lib/donate';
  import { showToast } from '../../lib/stores/toast';

  // /donate — three sections: one-time, monthly Pro tier picker, lifetime.
  // Each button creates a Stripe Checkout Session via the API and redirects
  // the browser to the hosted URL. Plain window.location.assign — no SPA
  // navigation, no XHR — so popup blockers stay quiet and Stripe handles
  // the back button cleanly.

  type MonthlyTier = 'monthly_5' | 'monthly_10' | 'monthly_20' | 'monthly_50';

  const MONTHLY_OPTIONS: ReadonlyArray<{ value: MonthlyTier; label: string }> = [
    { value: 'monthly_5', label: '$5' },
    { value: 'monthly_10', label: '$10' },
    { value: 'monthly_20', label: '$20' },
    { value: 'monthly_50', label: '$50' },
  ];

  let monthlyTier = $state<MonthlyTier>('monthly_5');
  // One per CTA so two buttons can spin in parallel without collisions
  // (rare in practice, but a clean state-machine beats a single boolean).
  let busySku = $state<DonateSku | null>(null);

  $effect(() => withPageTitle('Donate'));

  async function go(sku: DonateSku): Promise<void> {
    if (busySku) return;
    busySku = sku;
    try {
      const url = await startCheckout(sku);
      window.location.assign(url);
    } catch (err) {
      busySku = null;
      const msg = err instanceof ApiError && err.status === 401
        ? 'Sign in to start a monthly donation.'
        : "Couldn't start checkout. Try again in a moment.";
      showToast(msg, 'error');
    }
  }
</script>

<article class="donate">
  <h1 class="donate__title">Support AtlasGuess</h1>
  <p class="donate__lede">
    AtlasGuess is built and maintained by one person. There are no ads on
    the gameplay pages and there will not be. Donations cover hosting and
    keep the lights on.
  </p>

  <div class="donate__options">
    <Card padding="lg">
      <header class="donate__option-header">
        <h2 class="donate__option-title">One-time</h2>
        <p class="donate__option-sub">Any amount. Choose on the next screen.</p>
      </header>
      <Button
        variant="secondary"
        size="lg"
        block
        loading={busySku === 'one_time'}
        disabled={busySku !== null && busySku !== 'one_time'}
        onclick={() => go('one_time')}
      >
        Donate
      </Button>
    </Card>

    <Card padding="lg" elevated>
      <header class="donate__option-header">
        <h2 class="donate__option-title">Monthly &mdash; Pro supporter</h2>
        <p class="donate__option-sub">
          Recurring monthly donation. Cancel any time from your account.
        </p>
      </header>
      <div class="donate__tier-picker">
        <RadioGroup
          bind:value={monthlyTier}
          options={MONTHLY_OPTIONS}
          name="monthly-tier"
          ariaLabel="Monthly donation amount"
        />
      </div>
      <Button
        variant="primary"
        size="lg"
        block
        loading={busySku === monthlyTier}
        disabled={busySku !== null && busySku !== monthlyTier}
        onclick={() => go(monthlyTier)}
      >
        Become a monthly supporter
      </Button>
    </Card>

    <Card padding="lg">
      <header class="donate__option-header">
        <h2 class="donate__option-title">Lifetime &mdash; $250 once</h2>
        <p class="donate__option-sub">
          One payment, lifetime supporter badge that survives any future
          subscription changes.
        </p>
      </header>
      <Button
        variant="secondary"
        size="lg"
        block
        loading={busySku === 'lifetime'}
        disabled={busySku !== null && busySku !== 'lifetime'}
        onclick={() => go('lifetime')}
      >
        Become a lifetime supporter
      </Button>
    </Card>
  </div>

</article>
